import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { getPlatformAnalytics } from "../../api/analyticsService";
import { getClients } from "../../api/adminService";
import { formatCurrency, formatDate } from "./constants";
import {
  PeriodSelector,
  StatCard,
  SectionCard,
  EmptyState,
  TrafficSourceBars,
  LocationBars,
  VisitorGpsMap,
  TopProductsCard,
  CategoryPerformanceCard,
  StoreRankRow,
  IconBadge,
  formatCount,
  formatDayLabel,
} from "./analyticsShared";
import ClientActivityModal from "./ClientActivityModal";
import { HEADER_HEIGHT } from "./layout/layoutConstants";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "stores", label: "Top stores" },
  { id: "locations", label: "Locations" },
  { id: "categories", label: "Categories" },
  { id: "products", label: "Products" },
  { id: "clients", label: "Clients" },
];

const NAV_OFFSET = HEADER_HEIGHT + 56;

const PLATFORM_FILTERS = [
  { value: "all", label: "Both", icon: DevicesOutlinedIcon },
  { value: "Web", label: "Website", icon: LanguageOutlinedIcon },
  { value: "Mobile", label: "Mobile App", icon: PhoneIphoneRoundedIcon },
];

export default function Analytics() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const sectionRefs = useRef({});

  const customIncomplete = period === "custom" && (!customStart || !customEnd);

  useEffect(() => {
    if (customIncomplete) return;

    let active = true;
    setLoading(true);
    getPlatformAnalytics({
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
      platform: platformFilter === "all" ? undefined : platformFilter,
    })
      .then((result) => {
        if (active) {
          setData(result);
          setError("");
          setLastUpdated(new Date());
        }
      })
      .catch(() => {
        if (active) setError("Could not load analytics. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [period, customStart, customEnd, customIncomplete, platformFilter, refreshToken]);

  const handleRefresh = useCallback(() => {
    if (!loading) setRefreshToken((t) => t + 1);
  }, [loading]);

  const scrollToSection = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const visitorsSeries = useMemo(() => {
    if (!data?.overview?.visitorsSeries) return { labels: [], values: [] };
    return {
      labels: data.overview.visitorsSeries.map((p) => formatDayLabel(p.date)),
      values: data.overview.visitorsSeries.map((p) => p.count),
    };
  }, [data]);

  const goToStore = (storeId) => navigate(`/admin/analytics/stores/${storeId}`);
  const isRefreshing = loading && !!data;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              width: 44,
              height: 44,
              boxShadow: (theme) => `0 8px 20px ${theme.palette.primary.main}33`,
            }}
          >
            <InsightsRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              Client app visitors, traffic, and top-performing stores.
            </Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
          <ToggleButtonGroup
            value={platformFilter}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value) setPlatformFilter(value);
            }}
            sx={{ bgcolor: "background.paper" }}
          >
            {PLATFORM_FILTERS.map(({ value, label, icon: Icon }) => (
              <ToggleButton key={value} value={value} sx={{ px: 1.5, gap: 0.75, textTransform: "none", fontWeight: 700 }}>
                <Icon fontSize="small" />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <PeriodSelector
            period={period}
            onPeriodChange={setPeriod}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(start, end) => {
              setCustomStart(start);
              setCustomEnd(end);
            }}
          />
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: "flex-end", sm: "center" } }}>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" noWrap>
                Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={handleRefresh} disabled={loading || customIncomplete}>
                  <RefreshRoundedIcon
                    fontSize="small"
                    sx={
                      isRefreshing
                        ? { animation: "analytics-spin 0.8s linear infinite", "@keyframes analytics-spin": { to: { transform: "rotate(360deg)" } } }
                        : undefined
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ height: 3, mb: 2, borderRadius: 1, overflow: "hidden" }}>
        <Fade in={isRefreshing} unmountOnExit>
          <LinearProgress sx={{ height: 3 }} />
        </Fade>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 3,
            py: 1,
            px: 0.5,
            position: "sticky",
            top: HEADER_HEIGHT,
            zIndex: 5,
            bgcolor: "background.default",
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {SECTIONS.map((s) => (
            <Chip
              key={s.id}
              label={s.label}
              size="small"
              clickable
              onClick={() => scrollToSection(s.id)}
              sx={{ fontWeight: 600, flexShrink: 0, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
            />
          ))}
        </Stack>
      )}

      {customIncomplete ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            color: "text.secondary",
          }}
        >
          <EventRoundedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">Pick a start and end date to load custom range analytics.</Typography>
        </Box>
      ) : loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Box ref={(el) => (sectionRefs.current.overview = el)} sx={{ scrollMarginTop: NAV_OFFSET }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PhoneIphoneRoundedIcon}
                label="Visitors"
                value={formatCount(data.overview.visitorsCurrent)}
                changePercent={data.overview.visitorsChangePercent}
                helper={`${formatCount(data.overview.newVisitors)} new / ${formatCount(data.overview.returningVisitors)} returning`}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={InsightsRoundedIcon}
                label="App Opens"
                value={formatCount(data.overview.appOpensCurrent)}
                changePercent={data.overview.appOpensChangePercent}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={VisibilityRoundedIcon}
                label="Page Views"
                value={formatCount(data.overview.pageViewsCurrent)}
                changePercent={data.overview.pageViewsChangePercent}
                color="info"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PersonAddAltRoundedIcon}
                label="New Visitors"
                value={formatCount(data.overview.newVisitors)}
                color="success"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <SectionCard title="Visitors over time" subtitle="Distinct visitors per day">
                {visitorsSeries.values.length > 0 ? (
                  <LineChart
                    height={260}
                    series={[
                      {
                        data: visitorsSeries.values,
                        color: "#12B886",
                        area: true,
                        showMark: true,
                        curve: "monotoneX",
                      },
                    ]}
                    xAxis={[{ scaleType: "point", data: visitorsSeries.labels }]}
                    yAxis={[{ width: 40 }]}
                    grid={{ horizontal: true }}
                    margin={{ left: 0, right: 10, top: 20, bottom: 20 }}
                    sx={{
                      "& .MuiLineElement-root": { strokeWidth: 2.5 },
                      "& .MuiAreaElement-root": { fill: "url(#visitorsAreaGradient)" },
                      "& .MuiMarkElement-root": { fill: "#12B886", stroke: "#fff", strokeWidth: 1.5 },
                      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "#e2e8f0" },
                    }}
                  >
                    <defs>
                      <linearGradient id="visitorsAreaGradient" x1="50%" y1="0%" x2="50%" y2="100%">
                        <stop offset="0%" stopColor="#12B886" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="#12B886" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                  </LineChart>
                ) : (
                  <EmptyState text="No visitor activity recorded in this period yet." />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SectionCard
                title="Traffic sources"
                subtitle="Where store &amp; product views came from"
                action={<IconBadge icon={InsightsRoundedIcon} color="secondary" />}
              >
                <TrafficSourceBars sources={data.trafficSources} />
              </SectionCard>
            </Grid>
          </Grid>

          <Box ref={(el) => (sectionRefs.current.stores = el)} sx={{ scrollMarginTop: NAV_OFFSET }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard
                title="Top stores"
                subtitle="By sales revenue"
                action={<IconBadge icon={PaidRoundedIcon} color="success" />}
              >
                {data.topStoresBySales.length > 0 ? (
                  <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
                    {data.topStoresBySales.map((s, i) => (
                      <StoreRankRow
                        key={s.storeId}
                        store={s}
                        metricValue={formatCurrency(s.revenue)}
                        onClick={() => goToStore(s.storeId)}
                        rank={i + 1}
                      />
                    ))}
                  </Stack>
                ) : (
                  <EmptyState text="No sales in this period yet." />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard
                title="Top stores"
                subtitle="By visits"
                action={<IconBadge icon={VisibilityRoundedIcon} color="info" />}
              >
                {data.topStoresByVisits.length > 0 ? (
                  <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
                    {data.topStoresByVisits.map((s, i) => (
                      <StoreRankRow
                        key={s.storeId}
                        store={s}
                        metricValue={`${formatCount(s.visits)} visits`}
                        onClick={() => goToStore(s.storeId)}
                        rank={i + 1}
                      />
                    ))}
                  </Stack>
                ) : (
                  <EmptyState text="No store visits in this period yet." />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard
                title="Top stores"
                subtitle="By rating"
                action={<IconBadge icon={StarRoundedIcon} color="warning" />}
              >
                {data.topStoresByRating.length > 0 ? (
                  <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
                    {data.topStoresByRating.map((s, i) => (
                      <StoreRankRow
                        key={s.storeId}
                        store={s}
                        metricValue={`${s.rating.toFixed(1)} ★`}
                        onClick={() => goToStore(s.storeId)}
                        rank={i + 1}
                      />
                    ))}
                  </Stack>
                ) : (
                  <EmptyState text="No rated stores yet." />
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Box ref={(el) => (sectionRefs.current.locations = el)} sx={{ scrollMarginTop: NAV_OFFSET }} />
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Top cities"
                subtitle="Where visitors are based, by saved address"
                action={<IconBadge icon={LocationOnRoundedIcon} color="secondary" />}
              >
                <LocationCoverageNote location={data.visitorLocations} />
                <LocationBars
                  items={data.visitorLocations.topCities}
                  getLabel={(c) => c.cityNameEnglish || c.city}
                  getSublabel={(c) => c.districtName}
                  onItemClick={(c) => c.cityId && navigate(`/admin/analytics/cities/${c.cityId}`)}
                />
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Top districts"
                subtitle="Grouped by region, for targeting campaigns"
                action={<IconBadge icon={LocationOnRoundedIcon} color="secondary" />}
              >
                <LocationCoverageNote location={data.visitorLocations} />
                <LocationBars
                  items={data.visitorLocations.topDistricts}
                  getLabel={(d) => d.districtName}
                  onItemClick={(d) => navigate(`/admin/analytics/districts/${encodeURIComponent(d.districtName)}`)}
                />
              </SectionCard>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <SectionCard
              title="Visitor locations (GPS)"
              subtitle="Where visitors actually are, from device location on app open"
              action={<IconBadge icon={LocationOnRoundedIcon} color="secondary" />}
            >
              <VisitorGpsMap points={data.visitorGpsPoints} />
            </SectionCard>
          </Box>

          <Box ref={(el) => (sectionRefs.current.categories = el)} sx={{ scrollMarginTop: NAV_OFFSET, mt: 3 }}>
            <CategoryPerformanceCard data={data.categoryPerformance} />
          </Box>

          <Box ref={(el) => (sectionRefs.current.products = el)} sx={{ scrollMarginTop: NAV_OFFSET, mt: 3 }}>
            <TopProductsCard
              data={data.topProducts}
              metrics={["sold", "watched", "wishlisted"]}
              title="Top products"
              subtitle="Top 100 platform-wide, this period"
            />
          </Box>

          <Box ref={(el) => (sectionRefs.current.clients = el)} sx={{ scrollMarginTop: NAV_OFFSET, mt: 3 }}>
            <ClientsSection />
          </Box>
        </>
      ) : null}
    </Box>
  );
}

const CLIENTS_PAGE_SIZE = 10;

function ClientsSection() {
  const [clients, setClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getClients({ search: search || undefined, page, pageSize: CLIENTS_PAGE_SIZE })
      .then((result) => {
        if (!active) return;
        setClients(result.items);
        setTotalCount(result.totalCount);
        setError("");
      })
      .catch(() => {
        if (active) setError("Could not load clients.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [search, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / CLIENTS_PAGE_SIZE));

  return (
    <>
      <SectionCard
        title="Clients"
        subtitle="Every registered client — open one to see their app activity"
        action={<IconBadge icon={GroupRoundedIcon} color="primary" />}
      >
        <TextField
          placeholder="Search name, email or phone"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <EmptyState text={error} />
        ) : clients.length === 0 ? (
          <EmptyState text="No clients match this search." icon={GroupRoundedIcon} />
        ) : (
          <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
            {clients.map((c) => (
              <ClientRow key={c.userId} client={c} onClick={() => setSelectedClientId(c.userId)} />
            ))}
          </Stack>
        )}

        {!loading && !error && totalCount > CLIENTS_PAGE_SIZE && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatCount(totalCount)} clients · page {page} of {totalPages}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeftRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRightRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </SectionCard>

      <ClientActivityModal clientId={selectedClientId} onClose={() => setSelectedClientId(null)} />
    </>
  );
}

function ClientRow({ client, onClick }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      onClick={onClick}
      sx={{
        py: 1.25,
        px: 1,
        borderRadius: 1.5,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(15,23,42,0.03)" },
      }}
    >
      <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main", fontSize: 14 }}>
        {client.fullName?.[0]?.toUpperCase() || "?"}
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="body2" fontWeight={600} noWrap>
            {client.fullName}
          </Typography>
          {client.isBanned && (
            <Tooltip title={client.banReason || "Banned"}>
              <BlockRoundedIcon sx={{ fontSize: 14, color: "error.main" }} />
            </Tooltip>
          )}
          {client.warningCount > 0 && (
            <Tooltip title={`${client.warningCount} warning(s)`}>
              <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "warning.main" }} />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {client.email} {client.phone ? `· ${client.phone}` : ""}
        </Typography>
      </Box>
      <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={700}>
          {formatCount(client.orderCount)} orders
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Joined {formatDate(client.createdAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {client.lastEnteredAt ? `Last entered ${formatDate(client.lastEnteredAt)}` : "Never entered"}
        </Typography>
      </Stack>
    </Stack>
  );
}

function LocationCoverageNote({ location }) {
  if (!location || location.totalVisitors === 0) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`Based on ${formatCount(location.attributedVisitors)} of ${formatCount(location.totalVisitors)} visitors with a saved address`}
      sx={{ height: 22, fontSize: 11, fontWeight: 600, mb: 1.5 }}
    />
  );
}
